import { fg } from '@atlaskit/platform-feature-flags/fg';

type DynamicUiSourceWithContentId = {
	contentId?: string;
	contentType: string;
};

type CustomPageEditExperienceParams = {
	experience: 'remix_custom_edit';
};

type CustomPageEditRequestParams<TDynamicUiSource, TEditorContext> = {
	dynamicUiSource: TDynamicUiSource | undefined;
	editorContextFallback: TEditorContext | undefined;
	experienceParams: CustomPageEditExperienceParams | undefined;
};

/**
 * Applies the shared request contract for Custom Remix on an editable Confluence page.
 * Host integrations remain responsible for resolving their current page ID and editor context.
 */
export const getCustomPageEditRequestParams = <
	TDynamicUiSource extends DynamicUiSourceWithContentId,
	TEditorContext,
>({
	dynamicUiSource,
	dynamicUiType,
	editorContext,
	hasEditablePageContext,
	sourceContentId,
}: {
	dynamicUiSource: TDynamicUiSource | undefined;
	dynamicUiType: string | undefined;
	editorContext: TEditorContext | undefined;
	hasEditablePageContext: boolean;
	sourceContentId: string | undefined;
}): CustomPageEditRequestParams<TDynamicUiSource, TEditorContext> => {
	const dynamicUiSourceToSend = dynamicUiSource ? { ...dynamicUiSource } : undefined;

	// Never forward a potentially stale ID supplied by the selected fragment.
	if (dynamicUiSourceToSend) {
		delete dynamicUiSourceToSend.contentId;
	}

	if (
		dynamicUiSourceToSend &&
		dynamicUiType === 'custom' &&
		dynamicUiSourceToSend.contentType === 'page' &&
		sourceContentId &&
		hasEditablePageContext &&
		fg('aifc_remix_custom_page_edit')
	) {
		dynamicUiSourceToSend.contentId = sourceContentId;

		return {
			dynamicUiSource: dynamicUiSourceToSend,
			editorContextFallback: editorContext,
			experienceParams: { experience: 'remix_custom_edit' },
		};
	}

	return {
		dynamicUiSource: dynamicUiSourceToSend,
		editorContextFallback: undefined,
		experienceParams: undefined,
	};
};
